<script setup lang="ts">
import DomainVerification from '@/components/onboarding/DomainVerification.vue';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';
import BaseInput from '@/components/ui/inputs/BaseInput.vue';
import { TenantDetails } from '@/interfaces';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { GlobeAltIcon } from '@heroicons/vue/24/outline';
import { Mail, AlertCircle, AlertTriangle, MessageCircleWarning, ShieldAlert } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import ToggleWithIcon from '@/components/ui/toggles/ToggleWithIcon.vue';
import ToggleWithDescription from '@/components/ui/toggles/ToggleWithDescription.vue';

const props = defineProps<{
  tenant: TenantDetails
}>();

const subdomain = ref('');
const email = ref('');
const topLevel = ref(false);
const posmarkDomain = computed(() => props.tenant.tenantBranding?.postmarkappDomain)

async function upsertSubdomain() {
  const notif = useNotificationStore().emitNotification({ title: "Initialisation en cours...", type: "wait" });
  try {
    const subdomainUri = subdomain.value + '.' + props.tenant.domain;
    const emailAddress = email.value + '@' + props.tenant.domain;

    await adminAPI.initTenantSubdomain({ subdomain: subdomainUri, email: emailAddress });
    useNotificationStore().emitNotification({ title: "Nom de domaine initialisé", type: "success" });
  } finally {
    notif.duration = 0;
  }
}
</script>
<template>
  <div v-if="!posmarkDomain" class="w-full">
    <h1 class="text-2xl leading-8">Choisissez le sous domaine dédié à Upclaim</h1>
    <pre>{{ tenant.tenantBranding }}</pre>
    <div class="my-4 w-96">
      <BaseInput
        size="xl"
        placeholder="qualite"
        :absoluteText="'.' + tenant.domain"
        :value="tenant.tenantBranding?.subdomainUri ? tenant.tenantBranding?.subdomainUri.split('.')[0] : undefined"
        :icon="GlobeAltIcon"
        v-model="subdomain"
      />
      <p class="mt-2" v-if="subdomain">Site dédié: <span>{{ subdomain }}.{{ tenant.domain }}</span></p>
    </div>

    <h2 class="text-xl font-semibold leading-6 mt-14">Adresse email pour contacter les voyageurs</h2>
    <div class="my-4 w-96">
      <ToggleWithDescription v-model="topLevel" label="Top level domain ?" />
      <BaseInput
        size="xl"
        placeholder="reclamation"
        :absoluteText="'@' + (topLevel || !subdomain ? '' : `${subdomain}.` || '') + tenant.domain"
        :value="tenant.tenantBranding?.postmarkappDomain?.Name.split('.')[0]"
        :icon="Mail"
        v-model="email"
      />
      <p class="mt-2" v-if="email">Adresse email: <span>{{ email }}@{{ topLevel || !subdomain ? tenant.domain : `${subdomain}.${tenant.domain}` }}</span></p>
    </div>
    <div class="mt-12">
      <BaseButton
        size="xl"
        type="submit"
        :text="subdomain && posmarkDomain ? 'Modifier' : 'Valider'"
        :icon="subdomain && posmarkDomain ? MessageCircleWarning : undefined"
        @click="upsertSubdomain"
      />
    </div>
  </div>
  <div v-else>
    <DomainVerification />
  </div>
</template>
